import React, { createContext, useEffect } from 'react';
import { useSearchData } from '../../../data/SearchData';

type ToriBottomBannerDisplay = {
    filterListBottomRef: React.RefObject<HTMLDivElement> | null;
    bottomBannerRef: React.RefObject<HTMLDivElement> | null;
    shouldDisplayBottomBannerOnDesktop: boolean;
};

const ToriBottomBannerDisplayContext = createContext<ToriBottomBannerDisplay>({
    filterListBottomRef: null,
    bottomBannerRef: null,
    shouldDisplayBottomBannerOnDesktop: false,
});

type ToriBottomBannerDisplayProviderProps = {
    children: React.ReactNode;
};

export function ToriBottomBannerDisplayProvider(
    props: ToriBottomBannerDisplayProviderProps,
) {
    const { children } = props;
    const { isFetched } = useSearchData();
    const filterListBottomRef = React.useRef<HTMLDivElement | null>(null);
    const bottomBannerRef = React.useRef<HTMLDivElement | null>(null);
    const [
        shouldDisplayBottomBannerOnDesktop,
        setShouldDisplayBottomBannerOnDesktop,
    ] = React.useState(false);

    useEffect(() => {
        const filterListBottom = filterListBottomRef.current;
        const bottomBanner = bottomBannerRef.current;

        if (isFetched && filterListBottom && bottomBanner) {
            setShouldDisplayBottomBannerOnDesktop(
                filterListBottom.getBoundingClientRect().bottom <=
                    bottomBanner.getBoundingClientRect().top,
            );
        }
    }, [isFetched]);

    const value = React.useMemo(
        () => ({
            bottomBannerRef,
            filterListBottomRef,
            shouldDisplayBottomBannerOnDesktop,
        }),
        [
            bottomBannerRef,
            filterListBottomRef,
            shouldDisplayBottomBannerOnDesktop,
        ],
    );

    return (
        <ToriBottomBannerDisplayContext.Provider value={value}>
            {children}
        </ToriBottomBannerDisplayContext.Provider>
    );
}

export function useToriBottomBannerDisplay() {
    const context = React.useContext(ToriBottomBannerDisplayContext);
    if (typeof context === 'undefined') {
        throw new Error(
            'useToriBottomBannerDisplay must be used within an ToriBottomBannerDisplayProvider',
        );
    }
    return context;
}
